import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Scale } from 'lucide-react';

const LawCollegesSection = () => {
  const [selectedProgram, setSelectedProgram] = useState('UG');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedCollege, setSelectedCollege] = useState(null);

  const colleges = [
    {
      id: 1,
      name: "National Law School of India University (NLSIU)",
      location: "Bengaluru, India",
      exam: "CLAT (Common Law Admission Test)",
      type: "National Law University",
      description: "India's top-ranked law university known for its rigorous B.A. LL.B (Hons.), LL.M, and research programs. Offers extensive legal internships and moot court training."
    },
    {
      id: 2,
      name: "National Law University (NLU)",
      location: "Delhi, India",
      exam: "AILET (All India Law Entrance Test)",
      type: "National Law University",
      description: "One of India's most competitive law schools, focusing on legal research, corporate law, and public policy."
    },
    {
      id: 3,
      name: "NALSAR University of Law",
      location: "Hyderabad, India",
      exam: "CLAT",
      type: "National Law University",
      description: "Ranked among India's top 3 law schools. Known for constitutional law, international law, and human rights research."
    },
    {
      id: 4,
      name: "Symbiosis Law School",
      location: "Pune, India",
      exam: "SLAT (Symbiosis Law Admission Test)",
      type: "Private Law College",
      description: "A premier private law school offering strong placements, global exposure, and dual-degree options."
    },
    {
      id: 5,
      name: "Gujarat National Law University (GNLU)",
      location: "Gandhinagar, India",
      exam: "CLAT",
      type: "National Law University",
      description: "Focuses on corporate and international law with strong industry linkages and modern legal research."
    },
    {
      id: 6,
      name: "West Bengal National University of Juridical Sciences (WBNUJS)",
      location: "Kolkata, India",
      exam: "CLAT",
      type: "National Law University",
      description: "Renowned for academic excellence and strong alumni in judiciary, corporate law, and civil services."
    },
    {
      id: 7,
      name: "Harvard Law School",
      location: "Cambridge, USA",
      exam: "LSAT / GRE",
      type: "Ivy League University",
      description: "World's top-ranked law school offering JD, LLM, and SJD degrees. Focuses on global legal systems and interdisciplinary research."
    },
    {
      id: 8,
      name: "University of Oxford, Faculty of Law",
      location: "Oxford, United Kingdom",
      exam: "LNAT / Interview",
      type: "Global Elite University",
      description: "Oldest law faculty in the English-speaking world. Offers BA in Jurisprudence, BCL, and DPhil programs."
    },
    {
      id: 9,
      name: "Stanford Law School",
      location: "California, USA",
      exam: "LSAT / GRE",
      type: "Top US Law School",
      description: "Known for technology law, international law, and innovation-driven legal education."
    },
    {
      id: 10,
      name: "National University of Singapore (NUS) Law",
      location: "Singapore",
      exam: "LSAT / IELTS / Interview",
      type: "Asian Premier University",
      description: "Top-ranked law school in Asia offering strong international law, arbitration, and business law programs."
    },
    {
      id: 11,
      name: "University of Melbourne Law School",
      location: "Melbourne, Australia",
      exam: "LSAT / IELTS / TOEFL",
      type: "Australian Group of Eight University",
      description: "Renowned for its JD and LLM programs with high global employability and research focus."
    }
  ];

  const programData = {
    'UG': {
      fullName: "Undergraduate Program (5-Year Integrated BA LLB)",
      eligibility: "Candidates who have completed 10+2 or equivalent from a recognized board",
      minPercentage: "45% marks (40% for SC/ST)",
      ageLimit: "No upper age limit",
      examPattern: {
        totalMarks: 150,
        duration: "2 Hours",
        totalQuestions: 150,
        negativeMarking: "-0.25 per wrong answer",
        sections: [
          { name: "English Language", questions: 28, marks: 28, desc: "Comprehension passages, grammar, vocabulary", icon: "📚" },
          { name: "Current Affairs & GK", questions: 35, marks: 35, desc: "Recent events, static GK, current affairs", icon: "🌍" },
          { name: "Legal Reasoning", questions: 35, marks: 35, desc: "Legal propositions, application of principles", icon: "⚖️" },
          { name: "Logical Reasoning", questions: 28, marks: 28, desc: "Analytical & logical reasoning", icon: "🧩" },
          { name: "Quantitative Techniques", questions: 13, marks: 13, desc: "Class 10 level mathematics", icon: "🔢" }
        ]
      }
    },
    'PG': {
      fullName: "Postgraduate Program (LLM - 1 Year)",
      eligibility: "Candidates with LLB degree or equivalent from a recognized university",
      minPercentage: "50% marks in LLB",
      ageLimit: "No upper age limit",
      examPattern: {
        totalMarks: 150,
        duration: "2 Hours",
        totalQuestions: 120,
        negativeMarking: "-0.25 per wrong answer",
        sections: [
          { name: "Constitutional Law", questions: 30, marks: 30, icon: "📜" },
          { name: "Jurisprudence", questions: 30, marks: 30, icon: "⚖️" },
          { name: "Administrative Law", questions: 20, marks: 20, icon: "🏛️" },
          { name: "Law of Torts", questions: 20, marks: 20, icon: "⚠️" },
          { name: "Contract Law", questions: 20, marks: 20, icon: "📝" }
        ]
      }
    }
  };

  const coursePackages = [
    {
      name: "Foundation",
      tagline: "Long-term Preparation",
      duration: "12 Months",
      price: "45,000",
      originalPrice: "65,000",
      discount: "31% OFF",
      features: [
        "Complete Study Material (All Subjects)",
        "200+ Hours Video Lectures",
        "50 Full-Length Mock Tests",
        "Daily Current Affairs Updates",
        "Legal Reasoning Workshops",
        "Doubt Resolution via Chat",
        "Previous Year Papers (10 Years)",
        "Performance Analytics Dashboard"
      ],
      variant: "primary",
      popular: false
    },
    {
      name: "Crash Course",
      tagline: "Intensive Fast-Track",
      duration: "6 Months",
      price: "35,000",
      originalPrice: "50,000",
      discount: "30% OFF",
      features: [
        "Intensive Study Material",
        "150+ Hours Video Lectures",
        "80 Full-Length Mock Tests",
        "Weekly Current Affairs PDFs",
        "Legal Reasoning Master Classes",
        "WhatsApp Group Support",
        "Previous Year Papers (5 Years)",
        "Section-wise Test Series",
        "Strategy Sessions with Toppers"
      ],
      variant: "success",
      popular: true
    },
    {
      name: "Premium",
      tagline: "Complete Mentorship",
      duration: "12 Months",
      price: "75,000",
      originalPrice: "1,20,000",
      discount: "38% OFF",
      features: [
        "Everything in Foundation + Crash",
        "Personal Mentor (1-on-1 Sessions)",
        "Unlimited Mock Tests",
        "Live Interactive Classes",
        "University Selection Guidance",
        "Interview & GD Preparation",
        "Personalized Study Plan",
        "Direct Faculty Contact",
        "Offline Study Material",
        "Priority Doubt Clearing",
        "Career Counselling Sessions"
      ],
      variant: "warning",
      popular: false
    }
  ];

  const faqs = [
    {
      q: "What is the eligibility for CLAT UG?",
      a: "Candidates who have passed 10+2 or equivalent from a recognized board with at least 45% marks (40% for SC/ST) are eligible. There is no upper age limit."
    },
    {
      q: "Is there negative marking in CLAT?",
      a: "Yes, there is negative marking of 0.25 marks for each incorrect answer in CLAT 2025."
    },
    {
      q: "How many NLUs accept CLAT scores?",
      a: "Currently, 22 National Law Universities across India accept CLAT scores for admissions to their UG and PG programs."
    },
    {
      q: "What is a good score in CLAT?",
      a: "For top NLUs like NLSIU, NLU Delhi, NALSAR, you need 100+ marks. For other good NLUs, 80-90 marks are considered competitive."
    },
    {
      q: "Can I prepare for CLAT in 6 months?",
      a: "Yes, with dedicated preparation of 6-8 hours daily, proper strategy, and regular mock tests, 6 months is sufficient for CLAT preparation."
    },
    {
      q: "Which newspapers should I read for CLAT?",
      a: "The Hindu and Indian Express are highly recommended. Focus on editorials, national and international news, and legal affairs sections."
    }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Hero Section */}
      <div className="bg-dark text-white py-5" style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' }}>
        <div className="container">
          <div className="text-center py-5">
            <div className="mb-4">
              <span style={{ fontSize: '80px' }}>⚖️</span>
            </div>
            <h1 className="display-3 fw-bold mb-3">CLAT 2025 Preparation</h1>
            <p className="lead mb-2 text-info">Common Law Admission Test for National Law Universities</p>
            <p className="fs-5 mb-4 text-white-50">Your Gateway to India's Premier Law Schools & Global Law Universities</p>
            
            <div className="d-flex flex-wrap justify-content-center gap-3 mb-5">
              <button className="btn btn-warning btn-lg fw-bold px-5 py-3">
                Start Your Journey →
              </button>
              <button className="btn btn-outline-light btn-lg fw-bold px-5 py-3">
                ▶ Watch Demo
              </button>
            </div>

            <div className="row g-4 mt-4">
              {[
                { label: "Success Rate", value: "92%", icon: "📈" },
                { label: "Expert Faculty", value: "50+", icon: "👨‍🏫" },
                { label: "Mock Tests", value: "200+", icon: "📝" },
                { label: "Students", value: "10,000+", icon: "🎓" }
              ].map((stat, idx) => (
                <div key={idx} className="col-6 col-md-3">
                  <div className="card bg-white bg-opacity-10 border-light border-opacity-25 text-white">
                    <div className="card-body text-center">
                      <div className="mb-2" style={{ fontSize: '40px' }}>{stat.icon}</div>
                      <h3 className="display-6 fw-bold mb-1">{stat.value}</h3>
                      <small className="text-white-50">{stat.label}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Program Tabs */}
      <div className="bg-white shadow-sm position-relative border-bottom">
        <div className="container py-3">
          <div className="d-flex justify-content-center gap-3">
            <button
              onClick={() => setSelectedProgram('UG')}
              className={`btn btn-lg fw-bold ${selectedProgram === 'UG' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              🎓 Undergraduate (BA LLB)
            </button>
            <button
              onClick={() => setSelectedProgram('PG')}
              className={`btn btn-lg fw-bold ${selectedProgram === 'PG' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              📚 Postgraduate (LLM)
            </button>
          </div>
        </div>
      </div>

      <div className="container py-5">
        {/* Quick Info Banner */}
        <div className="mb-5">
          <div className="card bg-primary text-white shadow-lg">
            <div className="card-body p-4">
              <h2 className="text-center fw-bold mb-4">📋 {programData[selectedProgram].fullName}</h2>
              <div className="row g-4">
                <div className="col-md-3 text-center">
                  <div style={{ fontSize: '50px' }}>🎓</div>
                  <h5 className="fw-bold mt-3">Eligibility</h5>
                  <p className="mb-2">{programData[selectedProgram].eligibility}</p>
                  <span className="badge bg-warning text-dark">{programData[selectedProgram].minPercentage}</span>
                </div>
                <div className="col-md-3 text-center">
                  <div style={{ fontSize: '50px' }}>⏰</div>
                  <h5 className="fw-bold mt-3">Duration</h5>
                  <p className="mb-2">{programData[selectedProgram].examPattern.duration}</p>
                  <span className="badge bg-warning text-dark">{programData[selectedProgram].examPattern.totalQuestions} Questions</span>
                </div>
                <div className="col-md-3 text-center">
                  <div style={{ fontSize: '50px' }}>🏆</div>
                  <h5 className="fw-bold mt-3">Total Marks</h5>
                  <p className="mb-2">{programData[selectedProgram].examPattern.totalMarks} Marks</p>
                  <span className="badge bg-danger">{programData[selectedProgram].examPattern.negativeMarking}</span>
                </div>
                <div className="col-md-3 text-center">
                  <div style={{ fontSize: '50px' }}>🎯</div>
                  <h5 className="fw-bold mt-3">Age Limit</h5>
                  <p className="mb-2">{programData[selectedProgram].ageLimit}</p>
                  <span className="badge bg-success">✓ Open for All</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Exam Pattern */}
        <section className="mb-5">
          <h2 className="text-center display-5 fw-bold mb-4">📊 Exam Pattern - {selectedProgram}</h2>
          <p className="text-center text-muted fs-5 mb-4">Complete breakdown of the CLAT examination structure</p>
          
          <div className="row g-4">
            {programData[selectedProgram].examPattern.sections.map((section, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 shadow-sm border-0 hover-shadow">
                  <div className="card-body">
                    <div className="text-center mb-3" style={{ fontSize: '60px' }}>{section.icon}</div>
                    <h5 className="card-title fw-bold text-center mb-3">{section.name}</h5>
                    {section.desc && <p className="text-muted small text-center mb-3">{section.desc}</p>}
                    <div className="d-flex justify-content-around pt-3 border-top">
                      <div className="text-center">
                        <div className="h3 fw-bold text-primary mb-0">{section.questions}</div>
                        <small className="text-muted">Questions</small>
                      </div>
                      <div className="text-center">
                        <div className="h3 fw-bold text-success mb-0">{section.marks}</div>
                        <small className="text-muted">Marks</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Course Packages */}
        <section className="mb-5">
          <h2 className="text-center display-5 fw-bold mb-4">💎 Course Packages for CLAT 2025</h2>
          <p className="text-center text-muted fs-5 mb-5">Choose the perfect plan that fits your preparation needs</p>
          
          <div className="row g-4">
            {coursePackages.map((pkg, idx) => (
              <div key={idx} className="col-lg-4">
                <div className={`card h-100 shadow-lg ${pkg.popular ? 'border-warning border-3' : ''}`} style={{ position: 'relative' }}>
                  {pkg.popular && (
                    <div className="badge bg-warning text-dark position-absolute top-0 start-50 translate-middle px-3 py-2 fw-bold">
                      ⭐ MOST POPULAR
                    </div>
                  )}
                  
                  <div className={`card-header bg-${pkg.variant} text-white text-center py-4`}>
                    <h3 className="fw-bold mb-2">{pkg.name}</h3>
                    <p className="mb-3">{pkg.tagline}</p>
                    <div className="display-4 fw-bold mb-2">₹{pkg.price}</div>
                    <small className="text-decoration-line-through opacity-75">₹{pkg.originalPrice}</small>
                    <div className="badge bg-warning text-dark mt-2 px-3 py-2">{pkg.discount}</div>
                    <p className="mt-2 mb-0">{pkg.duration} Access</p>
                  </div>
                  
                  <div className="card-body">
                    <ul className="list-unstyled mb-4">
                      {pkg.features.map((feature, i) => (
                        <li key={i} className="mb-3">
                          <span className="text-success me-2">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <button className={`btn btn-${pkg.variant} w-100 py-3 fw-bold`}>
                      Enroll Now →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="alert alert-info mt-4 d-flex align-items-start">
            <span className="me-3" style={{ fontSize: '30px' }}>⚡</span>
            <div>
              <h5 className="fw-bold mb-2">💳 Flexible Payment Options Available</h5>
              <p className="mb-0">EMI options starting from ₹2,500/month • UPI • Credit/Debit Cards • Net Banking • 7-day Money Back Guarantee</p>
            </div>
          </div>
        </section>

        {/* Top Law Colleges */}
        <section className="mb-5">
          <h2 className="text-center display-5 fw-bold mb-4">🏛️ Top Law Colleges & Universities</h2>
          <p className="text-center text-muted fs-5 mb-5">Explore premier law institutions in India and abroad</p>
          
          <div className="row g-4">
            {colleges.map((college) => (
              <div key={college.id} className="col-md-6 col-lg-4">
                <div className="card h-100 shadow-sm border-0 hover-shadow">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title fw-bold mb-2">{college.name}</h5>
                    <p className="text-muted mb-2">📍 {college.location}</p>
                    <p className="text-secondary small mb-3">{college.type}</p>
                    <div className="mt-auto">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setSelectedCollege(college)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Success Stories */}
        <section className="mb-5">
          <h2 className="text-center display-5 fw-bold mb-4">🏆 Success Stories</h2>
          <p className="text-center text-muted fs-5 mb-5">Our students are at top NLUs across India</p>
          
          <div className="row g-4">
            {[
              { name: "Rahul Sharma", rank: "AIR 23", nlu: "NLSIU Bangalore", score: "118/150" },
              { name: "Priya Mehta", rank: "AIR 45", nlu: "NLU Delhi", score: "112/150" },
              { name: "Aditya Kumar", rank: "AIR 67", nlu: "NALSAR Hyderabad", score: "108/150" }
            ].map((student, idx) => (
              <div key={idx} className="col-md-4">
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-4">
                      <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3" 
                           style={{ width: '60px', height: '60px', fontSize: '24px', fontWeight: 'bold' }}>
                        {student.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <h5 className="fw-bold mb-1">{student.name}</h5>
                        <span className="badge bg-warning text-dark">🏆 {student.rank}</span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Selected at:</span>
                        <strong className="text-primary">{student.nlu}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Score:</span>
                        <strong className="text-success">{student.score}</strong>
                      </div>
                    </div>
                    <div className="border-top pt-3">
                      <p className="small text-muted fst-italic mb-0">
                        "The comprehensive study material and mock tests were instrumental in my success. The faculty's guidance was exceptional!"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-5">
          <h2 className="text-center display-5 fw-bold mb-4">❓ Frequently Asked Questions</h2>
          <p className="text-center text-muted fs-5 mb-5">Get answers to common CLAT preparation queries</p>
          
          <div className="card shadow-lg">
            <div className="card-body">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border-bottom pb-3 mb-3">
                  <button
                    className="btn btn-link text-decoration-none text-start w-100 d-flex justify-content-between align-items-center p-0"
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  >
                    <div className="d-flex align-items-start flex-grow-1">
                      <span className="badge bg-primary me-3">Q{idx + 1}</span>
                      <h6 className="fw-bold mb-0 text-dark">{faq.q}</h6>
                    </div>
                    <span className="text-muted">{expandedFaq === idx ? '▲' : '▼'}</span>
                  </button>
                  {expandedFaq === idx && (
                    <div className="mt-3 ms-5 text-muted">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="mb-5">
          <div className="card bg-dark text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <span style={{ fontSize: '80px' }}>⚖️</span>
              </div>
              <h2 className="display-4 fw-bold mb-4">Ready to Start Your Legal Career?</h2>
              <p className="lead mb-5 px-md-5">
                Join thousands of successful CLAT aspirants who achieved their dream of studying at top National Law Universities with our expert guidance
              </p>
              
              <div className="d-flex flex-wrap justify-content-center gap-3 mb-4">
                <button className="btn btn-warning btn-lg fw-bold px-5 py-3">
                  Enroll Now for {selectedProgram} →
                </button>
                <button className="btn btn-outline-light btn-lg fw-bold px-5 py-3">
                  💬 Schedule Free Counseling
                </button>
              </div>
              
              <div className="d-flex flex-wrap justify-content-center gap-4 small">
                {['Money Back Guarantee', '24/7 Support', 'Expert Faculty', 'Live Classes'].map((feature, idx) => (
                  <span key={idx}>✓ {feature}</span>
                ))}
              </div>
            </div>
          </div>
        </section>
        </div> 
        </div>
     
  );
};

export default LawCollegesSection;


// import React, { useState } from 'react';
// import { Scale, BookOpen, Award, Users, Target, TrendingUp, Globe, MapPin, Clock, DollarSign, CheckCircle, FileText, Calendar, Lightbulb, GraduationCap, Building, Star, Download, Video, MessageCircle, BarChart } from 'lucide-react';
// import { FaTrophy } from 'react-icons/fa';
// import { Button, Card, Col, Container, Modal, Row } from 'react-bootstrap';

// const LawCollegesSection = () => {
//   const [selectedProgram, setSelectedProgram] = useState('UG');
//   const [activeTab, setActiveTab] = useState('overview');

//     const [selectedCollege, setSelectedCollege] = useState(null);

//   const colleges = [
//     // ----- INDIAN LAW COLLEGES -----
//     {
//       id: 1,
//       name: "National Law School of India University (NLSIU)",
//       location: "Bengaluru, India",
//       exam: "CLAT (Common Law Admission Test)",
//       type: "National Law University",
//       description:
//         "India’s top-ranked law university known for its rigorous B.A. LL.B (Hons.), LL.M, and research programs. Offers extensive legal internships and moot court training.",
//     },
//     {
//       id: 2,
//       name: "National Law University (NLU)",
//       location: "Delhi, India",
//       exam: "AILET (All India Law Entrance Test)",
//       type: "National Law University",
//       description:
//         "One of India’s most competitive law schools, focusing on legal research, corporate law, and public policy.",
//     },
//     {
//       id: 3,
//       name: "NALSAR University of Law",
//       location: "Hyderabad, India",
//       exam: "CLAT",
//       type: "National Law University",
//       description:
//         "Ranked among India’s top 3 law schools. Known for constitutional law, international law, and human rights research.",
//     },
//     {
//       id: 4,
//       name: "Symbiosis Law School",
//       location: "Pune, India",
//       exam: "SLAT (Symbiosis Law Admission Test)",
//       type: "Private Law College",
//       description:
//         "A premier private law school offering strong placements, global exposure, and dual-degree options.",
//     },
//     {
//       id: 5,
//       name: "Gujarat National Law University (GNLU)",
//       location: "Gandhinagar, India",
//       exam: "CLAT",
//       type: "National Law University",
//       description:
//         "Focuses on corporate and international law with strong industry linkages and modern legal research.",
//     },
//     {
//       id: 6,
//       name: "West Bengal National University of Juridical Sciences (WBNUJS)",
//       location: "Kolkata, India",
//       exam: "CLAT",
//       type: "National Law University",
//       description:
//         "Renowned for academic excellence and strong alumni in judiciary, corporate law, and civil services.",
//     },

//     // ----- ABROAD UNIVERSITIES -----
//     {
//       id: 7,
//       name: "Harvard Law School",
//       location: "Cambridge, USA",
//       exam: "LSAT / GRE",
//       type: "Ivy League University",
//       description:
//         "World’s top-ranked law school offering JD, LLM, and SJD degrees. Focuses on global legal systems and interdisciplinary research.",
//     },
//     {
//       id: 8,
//       name: "University of Oxford, Faculty of Law",
//       location: "Oxford, United Kingdom",
//       exam: "LNAT / Interview",
//       type: "Global Elite University",
//       description:
//         "Oldest law faculty in the English-speaking world. Offers BA in Jurisprudence, BCL, and DPhil programs.",
//     },
//     {
//       id: 9,
//       name: "Stanford Law School",
//       location: "California, USA",
//       exam: "LSAT / GRE",
//       type: "Top US Law School",
//       description:
//         "Known for technology law, international law, and innovation-driven legal education.",
//     },
//     {
//       id: 10,
//       name: "National University of Singapore (NUS) Law",
//       location: "Singapore",
//       exam: "LSAT / IELTS / Interview",
//       type: "Asian Premier University",
//       description:
//         "Top-ranked law school in Asia offering strong international law, arbitration, and business law programs.",
//     },
//     {
//       id: 11,
//       name: "University of Melbourne Law School",
//       location: "Melbourne, Australia",
//       exam: "LSAT / IELTS / TOEFL",
//       type: "Australian Group of Eight University",
//       description:
//         "Renowned for its JD and LLM programs with high global employability and research focus.",
//     },
//   ];

//   const programData = {
//     'UG': {
//       fullName: "Undergraduate Program (5-Year Integrated BA LLB/BBA LLB/BSc LLB)",
//       eligibility: "Candidates who have completed 10+2 or equivalent from a recognized board",
//       ageLimit: "No upper age limit",
//       examPattern: {
//         totalMarks: 150,
//         duration: "2 Hours",
//         totalQuestions: 150,
//         sections: [
//           { name: "English Language", questions: 28, marks: 28, desc: "Comprehension passages, grammar, vocabulary" },
//           { name: "Current Affairs & GK", questions: 35, marks: 35, desc: "Recent events, static GK, current affairs" },
//           { name: "Legal Reasoning", questions: 35, marks: 35, desc: "Legal propositions, application of principles" },
//           { name: "Logical Reasoning", questions: 28, marks: 28, desc: "Analytical & logical reasoning" },
//           { name: "Quantitative Techniques", questions: 13, marks: 13, desc: "Class 10 level mathematics" }
//         ]
//       }
//     },
//     'PG': {
//       fullName: "Postgraduate Program (LLM - 1 Year)",
//       eligibility: "Candidates with LLB degree or equivalent from a recognized university",
//       ageLimit: "No upper age limit",
//       examPattern: {
//         totalMarks: 150,
//         duration: "2 Hours",
//         totalQuestions: 120,
//         sections: [
//           { name: "Constitutional Law", questions: 30, marks: 30 },
//           { name: "Jurisprudence", questions: 30, marks: 30 },
//           { name: "Administrative Law", questions: 20, marks: 20 },
//           { name: "Law of Torts", questions: 20, marks: 20 },
//           { name: "Contract Law", questions: 20, marks: 20 }
//         ]
//       }
//     }
//   };

//   const topNLUs = [
//     {
//       name: "National Law School of India University (NLSIU)",
//       location: "Bengaluru",
//       rank: "#1",
//       established: "1987",
//       highlights: ["First NLU in India", "Highest placements", "Premier moot court facility"],
//       avgPackage: "₹15-20 LPA"
//     },
//     {
//       name: "National Law University (NLU), Delhi",
//       location: "Delhi",
//       rank: "#2",
//       established: "2008",
//       highlights: ["Capital location advantage", "Strong corporate connections", "Excellent faculty"],
//       avgPackage: "₹12-18 LPA"
//     },
//     {
//       name: "NALSAR University of Law",
//       location: "Hyderabad",
//       rank: "#3",
//       established: "1998",
//       highlights: ["Strong research culture", "International collaborations", "Top-tier placements"],
//       avgPackage: "₹12-16 LPA"
//     },
//     {
//       name: "West Bengal NUJS",
//       location: "Kolkata",
//       rank: "#4",
//       established: "1999",
//       highlights: ["East India's premier law school", "Strong alumni network", "IPR specialization"],
//       avgPackage: "₹10-15 LPA"
//     },
//     {
//       name: "Gujarat National Law University (GNLU)",
//       location: "Gandhinagar",
//       rank: "#5",
//       established: "2003",
//       highlights: ["Corporate law focus", "Industry partnerships", "Modern infrastructure"],
//       avgPackage: "₹10-14 LPA"
//     }
//   ];

//   const internationalOptions = [
//     {
//       name: "Harvard Law School",
//       location: "USA",
//       rank: "#1 Globally",
//       exam: "LSAT",
//       avgFee: "$65,000/year",
//       highlights: ["World's most prestigious", "Supreme Court judges alumni", "Unmatched opportunities"]
//     },
//     {
//       name: "University of Oxford",
//       location: "UK",
//       rank: "#1 in UK",
//       exam: "LNAT",
//       avgFee: "£28,000/year",
//       highlights: ["Oldest law school", "Tutorial system", "Global recognition"]
//     },
//     {
//       name: "Stanford Law School",
//       location: "USA",
//       rank: "#2 in USA",
//       exam: "LSAT",
//       avgFee: "$64,000/year",
//       highlights: ["Silicon Valley proximity", "Tech law expertise", "Small class sizes"]
//     },
//     {
//       name: "National University of Singapore",
//       location: "Singapore",
//       rank: "#1 in Asia",
//       exam: "SAT/LSAT",
//       avgFee: "S$28,000/year",
//       highlights: ["Asian legal hub", "Affordable for quality", "Strong placement"]
//     }
//   ];

//   const syllabusTopics = {
//     'UG': [
//       {
//         subject: "English Language",
//         topics: ["Reading Comprehension", "Grammar & Usage", "Vocabulary", "Sentence Correction", "Para Jumbles", "Critical Reasoning"]
//       },
//       {
//         subject: "Current Affairs & General Knowledge",
//         topics: ["National & International News", "Indian Polity", "History & Culture", "Geography", "Economics", "Science & Technology", "Important Personalities"]
//       },
//       {
//         subject: "Legal Reasoning",
//         topics: ["Legal Principles & Cases", "Indian Constitution", "IPC & CrPC Basics", "Contract Act", "Tort Law", "Legal Maxims", "Application of Principles"]
//       },
//       {
//         subject: "Logical Reasoning",
//         topics: ["Analytical Reasoning", "Logical Sequences", "Syllogisms", "Blood Relations", "Coding-Decoding", "Seating Arrangement", "Puzzles"]
//       },
//       {
//         subject: "Quantitative Techniques",
//         topics: ["Number Systems", "Algebra", "Geometry", "Mensuration", "Statistics", "Ratio & Proportion", "Percentages"]
//       }
//     ],
//     'PG': [
//       {
//         subject: "Constitutional Law",
//         topics: ["Fundamental Rights", "DPSP", "Union & State Relations", "Emergency Provisions", "Constitutional Amendments", "Judicial Review"]
//       },
//       {
//         subject: "Jurisprudence",
//         topics: ["Legal Theory", "Schools of Jurisprudence", "Rights & Duties", "Justice & Law", "Legal Concepts"]
//       }
//     ]
//   };

//   const importantDates = [
//     { event: "Official Notification", date: "July 2025", status: "upcoming" },
//     { event: "Registration Opens", date: "August 2025", status: "upcoming" },
//     { event: "Last Date to Apply", date: "November 2025", status: "upcoming" },
//     { event: "Admit Card Release", date: "November 2025", status: "upcoming" },
//     { event: "CLAT Exam Date", date: "First Sunday of December 2025", status: "important" },
//     { event: "Answer Key Release", date: "Within 3-4 days", status: "upcoming" },
//     { event: "Result Declaration", date: "December End 2025", status: "upcoming" },
//     { event: "Counselling Process", date: "January 2026", status: "upcoming" }
//   ];

//   const preparationTips = [
//     {
//       icon: <BookOpen />,
//       title: "Read Newspapers Daily",
//       desc: "Focus on editorials from The Hindu, Indian Express for current affairs and English improvement",
//       color: "#FF6B6B"
//     },
//     {
//       icon: <Clock />,
//       title: "Time Management",
//       desc: "Practice solving 150 questions in 120 minutes with accuracy of 85%+",
//       color: "#4ECDC4"
//     },
//     {
//       icon: <Target />,
//       title: "Mock Tests Weekly",
//       desc: "Take at least 2-3 full-length mocks per week to build exam temperament",
//       color: "#45B7D1"
//     },
//     {
//       icon: <Lightbulb />,
//       title: "Legal Awareness",
//       desc: "Watch Supreme Court judgments, read bare acts, follow legal news portals",
//       color: "#FFA07A"
//     },
//     {
//       icon: <TrendingUp />,
//       title: "Analyze Performance",
//       desc: "Review every mock test thoroughly, identify weak areas and work on them",
//       color: "#98D8C8"
//     },
//     {
//       icon: <Users />,
//       title: "Peer Learning",
//       desc: "Join study groups, discuss legal reasoning questions, debate on current affairs",
//       color: "#F7B731"
//     }
//   ];

//   const coursePackages = [
//     {
//       name: "Foundation Course",
//       duration: "12 Months",
//       price: "₹45,000",
//       features: [
//         "Complete Study Material (All Subjects)",
//         "200+ Hours Video Lectures",
//         "50 Full-Length Mock Tests",
//         "Daily Current Affairs Updates",
//         "Legal Reasoning Workshops",
//         "Doubt Resolution via Chat",
//         "Previous Year Papers (10 Years)",
//         "Performance Analytics Dashboard"
//       ],
//       color: "from-blue-500 to-blue-700",
//       popular: false
//     },
//     {
//       name: "Crash Course",
//       duration: "6 Months",
//       price: "₹35,000",
//       features: [
//         "Intensive Study Material",
//         "150+ Hours Video Lectures",
//         "80 Full-Length Mock Tests",
//         "Weekly Current Affairs PDFs",
//         "Legal Reasoning Master Classes",
//         "WhatsApp Group Support",
//         "Previous Year Papers (5 Years)",
//         "Section-wise Test Series",
//         "Strategy Sessions with Toppers"
//       ],
//       color: "from-purple-500 to-purple-700",
//       popular: true
//     },
//     {
//       name: "Premium Mentorship",
//       duration: "12 Months",
//       price: "₹75,000",
//       features: [
//         "Everything in Foundation + Crash",
//         "Personal Mentor (1-on-1 Sessions)",
//         "Unlimited Mock Tests",
//         "Live Interactive Classes",
//         "University Selection Guidance",
//         "Interview & GD Preparation",
//         "Personalized Study Plan",
//         "Direct Faculty Contact",
//         "Offline Study Material",
//         "Priority Doubt Clearing",
//         "Career Counselling Sessions"
//       ],
//       color: "from-green-500 to-green-700",
//       popular: false
//     }
//   ];

//   const specializations = [
//     { name: "Corporate Law", icon: <Building />, demand: "Very High" },
//     { name: "Intellectual Property Rights", icon: <Lightbulb />, demand: "High" },
//     { name: "Criminal Law", icon: <Scale />, demand: "High" },
//     { name: "Constitutional Law", icon: <Award />, demand: "Medium" },
//     { name: "Cyber Law", icon: <Globe />, demand: "Growing" },
//     { name: "International Law", icon: <Globe />, demand: "Medium" }
//   ];

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
//       {/* Hero Section */}
//       <div className="relative bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white overflow-hidden">
//         <div className="absolute inset-0 opacity-10">
//           <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.3),rgba(255,255,255,0))]"></div>
//         </div>
        
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
//           <div className="text-center">
//             <div className="flex justify-center mb-6">
//               <Scale className="w-20 h-20 text-yellow-400" />
//             </div>
//             <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
//               CLAT Preparation Course
//             </h1>
//             <p className="text-xl md:text-2xl mb-4 text-blue-200">
//               Common Law Admission Test for National Law Universities
//             </p>
//             <p className="text-lg mb-8 text-gray-300 max-w-3xl mx-auto">
//               Your Gateway to India's Premier Law Schools & Global Law Universities
//             </p>
            
//             <div className="flex flex-wrap justify-center gap-4 mb-8">
//               <button 
//                 onClick={() => window.location.href = '/enroll'}
//                 className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-bold py-4 px-8 rounded-full transition transform hover:scale-105 shadow-lg"
//               >
//                 Start Your Journey
//               </button>
//               <button 
//                 onClick={() => setActiveTab('syllabus')}
//                 className="bg-transparent border-2 border-white hover:bg-white hover:text-slate-900 font-bold py-4 px-8 rounded-full transition"
//               >
//                 Explore Syllabus
//               </button>
//             </div>

//             <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 max-w-4xl mx-auto">
//               {[
//                 { label: "Success Rate", value: "92%", icon: <TrendingUp /> },
//                 { label: "Expert Faculty", value: "50+", icon: <Users /> },
//                 { label: "Mock Tests", value: "200+", icon: <FileText /> },
//                 { label: "Students Enrolled", value: "10,000+", icon: <GraduationCap /> }
//               ].map((stat, idx) => (
//                 <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
//                   <div className="text-yellow-400 mb-2 flex justify-center">{stat.icon}</div>
//                   <div className="text-3xl font-bold mb-1">{stat.value}</div>
//                   <div className="text-sm text-gray-300">{stat.label}</div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Program Selection */}
//       <div className="bg-white shadow-lg sticky top-0 z-50 border-b">
//         <div className="max-w-7xl mx-auto px-4 py-4">
//           <div className="flex justify-center gap-4">
//             {['UG', 'PG'].map(prog => (
//               <button
//                 key={prog}
//                 onClick={() => setSelectedProgram(prog)}
//                 className={`px-8 py-3 rounded-lg font-bold transition ${
//                   selectedProgram === prog
//                     ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
//                     : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
//                 }`}
//               >
//                 {prog === 'UG' ? 'Undergraduate (BA LLB)' : 'Postgraduate (LLM)'}
//               </button>
//             ))}
//           </div>
//         </div>
//       </div>

//       <div className="max-w-7xl mx-auto px-4 py-12">
//         {/* Quick Info Cards */}
//         <div className="grid md:grid-cols-3 gap-6 mb-12">
//           <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-blue-600">
//             <GraduationCap className="w-12 h-12 text-blue-600 mb-4" />
//             <h3 className="text-xl font-bold mb-2">Eligibility</h3>
//             <p className="text-gray-600">{programData[selectedProgram].eligibility}</p>
//           </div>
          
//           <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-purple-600">
//             <Clock className="w-12 h-12 text-purple-600 mb-4" />
//             <h3 className="text-xl font-bold mb-2">Duration</h3>
//             <p className="text-gray-600">{programData[selectedProgram].examPattern.duration} Exam Duration</p>
//             <p className="text-sm text-gray-500 mt-2">{programData[selectedProgram].examPattern.totalQuestions} Questions</p>
//           </div>
          
//           <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-green-600">
//             <Award className="w-12 h-12 text-green-600 mb-4" />
//             <h3 className="text-xl font-bold mb-2">Total Marks</h3>
//             <p className="text-gray-600">{programData[selectedProgram].examPattern.totalMarks} Marks</p>
//             <p className="text-sm text-gray-500 mt-2">No negative marking</p>
//           </div>
//         </div>

//         {/* Exam Pattern */}
//         <section className="mb-12">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <FileText className="inline mr-3 text-blue-600" />
//             Exam Pattern {selectedProgram}
//           </h2>
          
//           <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
//             <div className="overflow-x-auto">
//               <table className="w-full">
//                 <thead>
//                   <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
//                     <th className="px-6 py-4 text-left">Section</th>
//                     <th className="px-6 py-4 text-center">Questions</th>
//                     <th className="px-6 py-4 text-center">Marks</th>
//                     {selectedProgram === 'UG' && <th className="px-6 py-4 text-left">Description</th>}
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {programData[selectedProgram].examPattern.sections.map((section, idx) => (
//                     <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
//                       <td className="px-6 py-4 font-semibold text-gray-800">{section.name}</td>
//                       <td className="px-6 py-4 text-center text-blue-600 font-bold">{section.questions}</td>
//                       <td className="px-6 py-4 text-center text-green-600 font-bold">{section.marks}</td>
//                       {selectedProgram === 'UG' && <td className="px-6 py-4 text-gray-600 text-sm">{section.desc}</td>}
//                     </tr>
//                   ))}
//                   <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold">
//                     <td className="px-6 py-4">Total</td>
//                     <td className="px-6 py-4 text-center">{programData[selectedProgram].examPattern.totalQuestions}</td>
//                     <td className="px-6 py-4 text-center">{programData[selectedProgram].examPattern.totalMarks}</td>
//                     {selectedProgram === 'UG' && <td className="px-6 py-4"></td>}
//                   </tr>
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         </section>

//         {/* Top NLUs in India */}
//         <section className="mb-12">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <MapPin className="inline mr-3 text-blue-600" />
//             Top National Law Universities in India
//           </h2>
          
//           <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
//             {topNLUs.map((nlu, idx) => (
//               <div key={idx} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition p-6 border-t-4 border-blue-600">
//                 <div className="flex justify-between items-start mb-4">
//                   <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-bold text-sm">
//                     {nlu.rank}
//                   </div>
//                   <Star className="w-6 h-6 text-yellow-500 fill-current" />
//                 </div>
                
//                 <h3 className="text-xl font-bold mb-2 text-gray-800">{nlu.name}</h3>
//                 <div className="flex items-center text-gray-600 mb-2">
//                   <MapPin className="w-4 h-4 mr-2" />
//                   <span>{nlu.location}</span>
//                 </div>
//                 <div className="text-sm text-gray-500 mb-4">Est. {nlu.established}</div>
                
//                 <div className="space-y-2 mb-4">
//                   {nlu.highlights.map((highlight, i) => (
//                     <div key={i} className="flex items-start">
//                       <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
//                       <span className="text-sm text-gray-700">{highlight}</span>
//                     </div>
//                   ))}
//                 </div>
                
//                 <div className="pt-4 border-t">
//                   <div className="text-sm text-gray-600">Avg. Package</div>
//                   <div className="text-lg font-bold text-green-600">{nlu.avgPackage}</div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </section>

//         {/* International Options */}
//         <section className="mb-12">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <Globe className="inline mr-3 text-purple-600" />
//             Study Law Abroad - Top International Universities
//           </h2>
          
//           <div className="grid md:grid-cols-2 gap-6">
//             {internationalOptions.map((uni, idx) => (
//               <div key={idx} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl shadow-lg hover:shadow-2xl transition p-6">
//                 <div className="flex justify-between items-start mb-4">
//                   <div className="bg-purple-600 text-white px-4 py-2 rounded-full font-bold text-sm">
//                     {uni.rank}
//                   </div>
//                   <Globe className="w-6 h-6 text-purple-600" />
//                 </div>
                
//                 <h3 className="text-2xl font-bold mb-2 text-gray-800">{uni.name}</h3>
//                 <div className="flex items-center text-gray-600 mb-4">
//                   <MapPin className="w-4 h-4 mr-2" />
//                   <span className="font-semibold">{uni.location}</span>
//                 </div>
                
//                 <div className="grid grid-cols-2 gap-4 mb-4">
//                   <div>
//                     <div className="text-sm text-gray-600">Required Exam</div>
//                     <div className="font-bold text-purple-700">{uni.exam}</div>
//                   </div>
//                   <div>
//                     <div className="text-sm text-gray-600">Avg. Fee</div>
//                     <div className="font-bold text-green-600">{uni.avgFee}</div>
//                   </div>
//                 </div>
                
//                 <div className="space-y-2">
//                   {uni.highlights.map((highlight, i) => (
//                     <div key={i} className="flex items-start">
//                       <CheckCircle className="w-4 h-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
//                       <span className="text-sm text-gray-700">{highlight}</span>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             ))}
//           </div>
          
//           <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg">
//             <h4 className="font-bold text-lg mb-2 text-gray-800">📚 Studying Abroad Requirements:</h4>
//             <ul className="space-y-2 text-gray-700">
//               <li>• LSAT/LNAT for USA/UK universities</li>
//               <li>• TOEFL/IELTS for English proficiency</li>
//               <li>• Strong academic record (70%+ in graduation)</li>
//               <li>• Statement of Purpose & Letters of Recommendation</li>
//               <li>• Bar Council of India recognition for practicing in India</li>
//             </ul>
//           </div>
//         </section>

//             <section className="py-5 bg-light" id="law-colleges">
//       <Container>
//         <h2 className="text-center mb-4 fw-bold">
//           Top Law Colleges & Universities
//         </h2>
//         <p className="text-center mb-5 text-muted">
//           Explore premier law institutions in India and abroad where our
//           admission training programs prepare students for success.
//         </p>

//         <Row xs={1} md={3} className="g-4">
//           {colleges.map((college) => (
//             <Col key={college.id}>
//               <Card className="h-100 shadow-sm border-0">
//                 <Card.Body className="d-flex flex-column justify-content-between">
//                   <div>
//                     <Card.Title className="fw-bold">
//                       {college.name}
//                     </Card.Title>
//                     <Card.Subtitle className="mb-2 text-muted">
//                       {college.location}
//                     </Card.Subtitle>
//                     <Card.Text className="text-secondary small">
//                       {college.type}
//                     </Card.Text>
//                   </div>
//                   <div className="mt-3">
//                     <Button
//                       variant="primary"
//                       size="sm"
//                       onClick={() => setSelectedCollege(college)}
//                     >
//                       View Details
//                     </Button>
//                   </div>
//                 </Card.Body>
//               </Card>
//             </Col>
//           ))}
//         </Row>

//         {/* Modal Popup for Details */}
//         <Modal
//           show={!!selectedCollege}
//           onHide={() => setSelectedCollege(null)}
//           centered
//         >
//           <Modal.Header closeButton>
//             <Modal.Title>
//               {selectedCollege?.name}
//             </Modal.Title>
//           </Modal.Header>
//           <Modal.Body>
//             <p>
//               <strong>Location:</strong> {selectedCollege?.location}
//             </p>
//             <p>
//               <strong>Entrance Exam:</strong> {selectedCollege?.exam}
//             </p>
//             <p>
//               <strong>Type:</strong> {selectedCollege?.type}
//             </p>
//             <p>{selectedCollege?.description}</p>
//             <hr />
//             <p className="text-muted small">
//               Our specialized law admission training program helps students
//               prepare for {selectedCollege?.exam} and develop academic,
//               analytical, and legal writing skills required for admission to{" "}
//               {selectedCollege?.name}.
//             </p>
//           </Modal.Body>
//           <Modal.Footer>
//             <Button variant="secondary" onClick={() => setSelectedCollege(null)}>
//               Close
//             </Button>
//           </Modal.Footer>
//         </Modal>
//       </Container>
//     </section>

//         {/* Detailed Syllabus */}
//         <section className="mb-12" id="syllabus">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <BookOpen className="inline mr-3 text-blue-600" />
//             Detailed Syllabus - {selectedProgram}
//           </h2>
          
//           <div className="grid md:grid-cols-2 gap-6">
//             {syllabusTopics[selectedProgram].map((subject, idx) => (
//               <div key={idx} className="bg-white rounded-2xl shadow-lg p-6">
//                 <h3 className="text-2xl font-bold mb-4 text-blue-700 border-b-2 border-blue-200 pb-3">
//                   {subject.subject}
//                 </h3>
//                 <div className="space-y-3">
//                   {subject.topics.map((topic, i) => (
//                     <div key={i} className="flex items-start">
//                       <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
//                       <span className="text-gray-700">{topic}</span>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             ))}
//           </div>
//         </section>

//         {/* Important Dates */}
//         <section className="mb-12">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <Calendar className="inline mr-3 text-blue-600" />
//             CLAT 2025 Important Dates
//           </h2>
          
//           <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
//             <div className="overflow-x-auto">
//               <table className="w-full">
//                 <thead>
//                   <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
//                     <th className="px-6 py-4 text-left">Event</th>
//                     <th className="px-6 py-4 text-right">Timeline</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {importantDates.map((item, idx) => (
//                     <tr key={idx} className={`${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'} ${item.status === 'important' ? 'bg-yellow-50' : ''}`}>
//                       <td className="px-6 py-4">
//                         <div className="flex items-center">
//                           <Calendar className="w-5 h-5 text-blue-600 mr-3" />
//                           <span className="font-semibold text-gray-800">{item.event}</span>
//                         </div>
//                       </td>
//                       <td className="px-6 py-4 text-right">
//                         <span className={`font-bold ${item.status === 'important' ? 'text-red-600 text-lg' : 'text-gray-700'}`}>
//                           {item.date}
//                         </span>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         </section>

//         {/* Preparation Tips */}
//         <section className="mb-12">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <Lightbulb className="inline mr-3 text-yellow-500" />
//             Expert Preparation Tips
//           </h2>
          
//           <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
//             {preparationTips.map((tip, idx) => (
//               <div 
//                 key={idx} 
//                 className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition p-6 border-t-4"
//                 style={{ borderColor: tip.color }}
//               >
//                 <div className="mb-4" style={{ color: tip.color }}>
//                   {React.cloneElement(tip.icon, { className: "w-12 h-12" })}
//                 </div>
//                 <h3 className="text-xl font-bold mb-3 text-gray-800">{tip.title}</h3>
//                 <p className="text-gray-600 leading-relaxed">{tip.desc}</p>
//               </div>
//             ))}
//           </div>
//         </section>

//         {/* Law Specializations */}
//         <section className="mb-12">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <Target className="inline mr-3 text-blue-600" />
//             Popular Law Specializations
//           </h2>
          
//           <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
//             {specializations.map((spec, idx) => (
//               <div key={idx} className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg hover:shadow-2xl transition p-6">
//                 <div className="text-blue-600 mb-4">
//                   {React.cloneElement(spec.icon, { className: "w-10 h-10" })}
//                 </div>
//                 <h3 className="text-xl font-bold mb-2 text-gray-800">{spec.name}</h3>
//                 <div className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
//                   Demand: {spec.demand}
//                 </div>
//               </div>
//             ))}
//           </div>
//         </section>

//         {/* Course Packages */}
//         <section className="mb-12">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <DollarSign className="inline mr-3 text-green-600" />
//             Course Packages for CLAT 2025
//           </h2>
          
//           <div className="grid md:grid-cols-3 gap-8">
//             {coursePackages.map((pkg, idx) => (
//               <div 
//                 key={idx} 
//                 className={`bg-white rounded-2xl shadow-xl overflow-hidden transform transition hover:scale-105 ${
//                   pkg.popular ? 'ring-4 ring-yellow-400 relative' : ''
//                 }`}
//               >
//                 {pkg.popular && (
//                   <div className="absolute top-0 right-0 bg-yellow-400 text-gray-900 px-4 py-1 rounded-bl-lg font-bold text-sm z-10">
//                     ⭐ MOST POPULAR
//                   </div>
//                 )}
                
//                 <div className={`bg-gradient-to-r ${pkg.color} text-white p-8 text-center`}>
//                   <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
//                   <div className="text-5xl font-bold my-4">{pkg.price}</div>
//                   <div className="text-lg opacity-90">{pkg.duration}</div>
//                 </div>
                
//                 <div className="p-6">
//                   <ul className="space-y-3 mb-6">
//                     {pkg.features.map((feature, i) => (
//                       <li key={i} className="flex items-start">
//                         <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
//                         <span className="text-gray-700">{feature}</span>
//                       </li>
//                     ))}
//                   </ul>
                  
//                   <button 
//                     onClick={() => window.location.href = '/enroll'}
//                     className={`w-full bg-gradient-to-r ${pkg.color} text-white font-bold py-4 rounded-xl hover:shadow-lg transition transform hover:scale-105`}
//                   >
//                     Enroll Now
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </section>

//         {/* Resources Section */}
//         <section className="mb-12">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <Download className="inline mr-3 text-blue-600" />
//             Free Resources & Materials
//           </h2>
          
//           <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
//             {[
//               { title: "Previous Year Papers", count: "10 Years", icon: <FileText />, color: "blue" },
//               { title: "Mock Tests", count: "50+ Tests", icon: <Award />, color: "purple" },
//               { title: "Video Lectures", count: "200+ Hours", icon: <Video />, color: "red" },
//               { title: "Study Material PDFs", count: "500+ Pages", icon: <BookOpen />, color: "green" }
//             ].map((resource, idx) => (
//               <div key={idx} className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition p-6 text-center cursor-pointer border-t-4 border-${resource.color}-500`}>
//                 <div className={`text-${resource.color}-600 mb-4 flex justify-center`}>
//                   {React.cloneElement(resource.icon, { className: "w-12 h-12" })}
//                 </div>
//                 <h3 className="text-xl font-bold mb-2 text-gray-800">{resource.title}</h3>
//                 <div className="text-gray-600 mb-4">{resource.count}</div>
//                 <button className={`bg-${resource.color}-100 text-${resource.color}-700 px-4 py-2 rounded-lg font-semibold hover:bg-${resource.color}-200 transition`}>
//                   Download Free
//                 </button>
//               </div>
//             ))}
//           </div>
//         </section>

//         {/* Success Stories */}
//         <section className="mb-12">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <Star className="inline mr-3 text-yellow-500" />
//             Success Stories
//           </h2>
          
//           <div className="grid md:grid-cols-3 gap-6">
//             {[
//               { name: "Rahul Sharma", rank: "AIR 23", nlu: "NLSIU Bangalore", score: "118/150" },
//               { name: "Priya Mehta", rank: "AIR 45", nlu: "NLU Delhi", score: "112/150" },
//               { name: "Aditya Kumar", rank: "AIR 67", nlu: "NALSAR Hyderabad", score: "108/150" }
//             ].map((student, idx) => (
//               <div key={idx} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl shadow-lg p-6">
//                 <div className="flex items-center mb-4">
//                   <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
//                     {student.name.split(' ').map(n => n[0]).join('')}
//                   </div>
//                   <div className="ml-4">
//                     <h3 className="font-bold text-lg text-gray-800">{student.name}</h3>
//                     <div className="text-yellow-600 font-semibold">{student.rank}</div>
//                   </div>
//                 </div>
//                 <div className="space-y-2">
//                   <div className="flex justify-between">
//                     <span className="text-gray-600">Selected at:</span>
//                     <span className="font-bold text-blue-700">{student.nlu}</span>
//                   </div>
//                   <div className="flex justify-between">
//                     <span className="text-gray-600">Score:</span>
//                     <span className="font-bold text-green-600">{student.score}</span>
//                   </div>
//                 </div>
//                 <div className="mt-4 pt-4 border-t border-gray-200">
//                   <p className="text-sm text-gray-600 italic">
//                     "The comprehensive study material and mock tests were instrumental in my success. The faculty's guidance was exceptional!"
//                   </p>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </section>

//         {/* FAQ Section */}
//         <section className="mb-12">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <MessageCircle className="inline mr-3 text-blue-600" />
//             Frequently Asked Questions
//           </h2>
          
//           <div className="bg-white rounded-2xl shadow-xl p-8">
//             <div className="space-y-6">
//               {[
//                 {
//                   q: "What is the eligibility for CLAT UG?",
//                   a: "Candidates who have passed 10+2 or equivalent from a recognized board with at least 45% marks (40% for SC/ST) are eligible. There is no upper age limit."
//                 },
//                 {
//                   q: "Is there negative marking in CLAT?",
//                   a: "Yes, there is negative marking of 0.25 marks for each incorrect answer in CLAT 2025."
//                 },
//                 {
//                   q: "How many NLUs accept CLAT scores?",
//                   a: "Currently, 22 National Law Universities across India accept CLAT scores for admissions to their UG and PG programs."
//                 },
//                 {
//                   q: "What is a good score in CLAT?",
//                   a: "For top NLUs like NLSIU, NLU Delhi, NALSAR, you need 100+ marks. For other good NLUs, 80-90 marks are considered competitive."
//                 },
//                 {
//                   q: "Can I prepare for CLAT in 6 months?",
//                   a: "Yes, with dedicated preparation of 6-8 hours daily, proper strategy, and regular mock tests, 6 months is sufficient for CLAT preparation."
//                 },
//                 {
//                   q: "Which newspapers should I read for CLAT?",
//                   a: "The Hindu and Indian Express are highly recommended. Focus on editorials, national and international news, and legal affairs sections."
//                 },
//                 {
//                   q: "Do I need coaching for CLAT?",
//                   a: "While coaching is not mandatory, it provides structured guidance, study material, mock tests, and expert mentorship which significantly improve your preparation."
//                 },
//                 {
//                   q: "What is the syllabus for CLAT 2025?",
//                   a: "CLAT UG covers English Language, Current Affairs & GK, Legal Reasoning, Logical Reasoning, and Quantitative Techniques. The syllabus is passage-based and comprehension-oriented."
//                 }
//               ].map((faq, idx) => (
//                 <div key={idx} className="border-b border-gray-200 pb-6 last:border-b-0">
//                   <h3 className="text-lg font-bold text-blue-700 mb-3 flex items-start">
//                     <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full mr-3 text-sm">Q{idx + 1}</span>
//                     {faq.q}
//                   </h3>
//                   <p className="text-gray-700 ml-12 leading-relaxed">{faq.a}</p>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </section>

//         {/* Statistics Dashboard */}
//         <section className="mb-12">
//           <h2 className="text-4xl font-bold text-center mb-8 text-gray-800">
//             <BarChart className="inline mr-3 text-blue-600" />
//             Our Track Record
//           </h2>
          
//           <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
//             {[
//               { label: "Students in Top 100", value: "47", icon: <FaTrophy />, color: "yellow" },
//               { label: "Average Score Improvement", value: "+32", icon: <TrendingUp />, color: "green" },
//               { label: "Success Rate", value: "92%", icon: <Target />, color: "blue" },
//               { label: "Years of Experience", value: "12+", icon: <Award />, color: "purple" }
//             ].map((stat, idx) => (
//               <div key={idx} className="bg-white rounded-2xl shadow-lg p-6 text-center">
//                 <div className={`text-${stat.color}-600 mb-4 flex justify-center`}>
//                   {React.cloneElement(stat.icon, { className: "w-12 h-12" })}
//                 </div>
//                 <div className={`text-4xl font-bold mb-2 text-${stat.color}-700`}>{stat.value}</div>
//                 <div className="text-gray-600">{stat.label}</div>
//               </div>
//             ))}
//           </div>
//         </section>

//         {/* Final CTA */}
//         <section className="mb-12">
//           <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 rounded-3xl shadow-2xl p-12 text-center text-white">
//             <h2 className="text-4xl md:text-5xl font-bold mb-6">
//               Start Your Legal Career Today!
//             </h2>
//             <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
//               Join thousands of successful CLAT aspirants who achieved their dream of studying at top National Law Universities with our expert guidance
//             </p>
            
//             <div className="flex flex-wrap justify-center gap-6 mb-8">
//               <button 
//                 onClick={() => window.location.href = '/enroll'}
//                 className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-5 px-10 rounded-full text-lg transition transform hover:scale-105 shadow-xl"
//               >
//                 Enroll in Course Now
//               </button>
//               <button 
//                 onClick={() => window.location.href = '/contact'}
//                 className="bg-white/20 backdrop-blur-sm hover:bg-white hover:text-blue-700 border-2 border-white font-bold py-5 px-10 rounded-full text-lg transition"
//               >
//                 Schedule Free Counseling
//               </button>
//             </div>
            
//             <div className="flex flex-wrap justify-center gap-8 text-sm">
//               <div className="flex items-center">
//                 <CheckCircle className="w-5 h-5 mr-2" />
//                 <span>Money Back Guarantee</span>
//               </div>
//               <div className="flex items-center">
//                 <CheckCircle className="w-5 h-5 mr-2" />
//                 <span>24/7 Support</span>
//               </div>
//               <div className="flex items-center">
//                 <CheckCircle className="w-5 h-5 mr-2" />
//                 <span>Expert Faculty</span>
//               </div>
//             </div>
//           </div>
//         </section>

//         {/* Contact Information */}
//         <section>
//           <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
//             <h3 className="text-2xl font-bold mb-4 text-gray-800">Need More Information?</h3>
//             <p className="text-gray-600 mb-6">
//               Our counselors are available to help you choose the right course and answer all your questions
//             </p>
//             <div className="flex flex-wrap justify-center gap-6">
//               <div className="flex items-center text-gray-700">
//                 <MessageCircle className="w-5 h-5 mr-2 text-blue-600" />
//                 <span className="font-semibold">+91 9876543210</span>
//               </div>
//               <div className="flex items-center text-gray-700">
//                 <Globe className="w-5 h-5 mr-2 text-blue-600" />
//                 <span className="font-semibold">www.clatprep.com</span>
//               </div>
//             </div>
//           </div>
//         </section>
//       </div>

//       {/* Footer */}
//       <footer className="bg-slate-900 text-white py-12 mt-12">
//         <div className="max-w-7xl mx-auto px-4 text-center">
//           <div className="mb-6">
//             <Scale className="w-12 h-12 mx-auto text-yellow-400 mb-4" />
//             <h3 className="text-2xl font-bold mb-2">CLAT Excellence Academy</h3>
//             <p className="text-gray-400">Your Gateway to Premier Law Education</p>
//           </div>
          
//           <div className="border-t border-gray-700 pt-6">
//             <p className="text-gray-400 text-sm">
//               Official CLAT Website: <a href="https://consortiumofnlus.ac.in" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">consortiumofnlus.ac.in</a>
//             </p>
//             <p className="text-gray-500 text-xs mt-4">
//               © 2025 CLAT Excellence Academy. All rights reserved. | Disclaimer: We are an independent coaching institute and not affiliated with CLAT authorities.
//             </p>
//           </div>
//         </div>
//       </footer>
//     </div>
//   );
// };

// export default LawCollegesSection;